import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_ALL_DRIVERS_BY_REGION, GET_DRIVERS_IN_STANDBY } from '../../graphql/client/queries';
import {
    AssignDriverToLoad,
    GetAllDrivers,
    GetAllDrivers_getAllDrivers as Driver,
    // GetStandbyDrivers_getStandbyDrivers as standByDrivers,
    GetDriversInStandby,
    GetDriversInStandby_getDriversInStandby as standByDrivers,
} from '../../types/schemaTypes';
import './AddDriverModal.scss';
import Modal from 'react-bootstrap/Modal';
import ModalHeaderTitle from './ModalHeaderTitle';
import DriverItem from './DriverItem';
import { useStoreContext } from '../App/StoreProvider';
import { Loading, Error } from '../_common';
import { NoResult } from '../../components/_common/NoResult/NoResult';
import * as Actions from '../../store/actions/addLoadModal';
import DispatchModal from '../AddLoads/DispatchModal';
import { OverlayTrigger, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ASSIGN_DRIVER_TO_LOAD } from '../../graphql/client/mutations';

interface DriverItemProps {
    driverName: string;
    driverId: number;
}

interface AddDriverModalProps {
    proppantName?: string | undefined;
    lofa?: string | undefined;
    lofaId: number;
    wellhead?: string | undefined;
    load?: string;
    jobId: number | null | undefined;
    onClickHide: () => void;
    onClickBack: (isSplit?: boolean) => void;
    show: boolean;
    isFromLoads?: boolean;
    isSplitLoad?: boolean;
    region?: {
        regionName: string | null;
        id: number | null;
        extSystemName: string | null;
        extSystemReference: string | null;
        regionCode: string | null;
    } | null;
    sandPO?: string;
    driverName?: string;
    isFromAddLoads?: boolean;
    driverIds?: number[];
}

const AddDriverModal = ({
    show,
    onClickHide,
    onClickBack,
    jobId,
    isFromLoads = false,
    load,
    region,
    proppantName,
    lofa,
    lofaId,
    wellhead,
    isSplitLoad = false,
    sandPO,
    driverName,
    isFromAddLoads,
    driverIds,
}: AddDriverModalProps): React.ReactElement => {
    const regionId = region ? region!.id : 0;
    const isPrimaryDriver = !isSplitLoad || false;
    // const [state] = useStoreContext();
    const [driverItem, setDriverItem] = useState<DriverItemProps>({ driverName: '', driverId: 0 });
    const [state, dispatch] = useStoreContext();
    const [showDispatchModal, setShowDispatchModal] = useState<boolean>(false);
    const [searchPhrase, setSearchPhrase] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const {
        addLoadModal: { currentLoadId },
    } = state;

    const loadId = isFromLoads ? load : String(currentLoadId);

    const { loading, error, data, refetch } = useQuery<GetAllDrivers>(GET_ALL_DRIVERS_BY_REGION, {
        variables: { regionId },
        fetchPolicy: 'network-only',
    });

    const { data: standbyDriverData, refetch: standByDriversRefetch } = useQuery<GetDriversInStandby>(
        GET_DRIVERS_IN_STANDBY,
        {
            variables: {
                jobId: jobId,
                lofaId: lofaId,
            },
            fetchPolicy: 'network-only',
            pollInterval: 300000,
        },
    );

    const standbyDrivers: standByDrivers[] = [];
    if (
        standbyDriverData &&
        standbyDriverData.getDriversInStandby &&
        standbyDriverData.getDriversInStandby.length !== 0
    ) {
        standbyDriverData.getDriversInStandby.forEach((element: standByDrivers | null): void => {
            if (element !== null && !driverIds?.includes(element.driverId ?? 0)) {
                standbyDrivers.push(element);
            }
        });
    }

    const onAssignDriver = (data: AssignDriverToLoad): void => {
        if (data.assignDriverToLoad === null) return;
        const assignDriverErrors = data.assignDriverToLoad.errors;
        if (assignDriverErrors && assignDriverErrors.length > 0) {
            setErrorMessage(assignDriverErrors[0] || '');
        } else {
            if (refetch) {
                refetch();
            }
            if (standByDriversRefetch) {
                standByDriversRefetch();
            }
            dispatch(
                Actions.assignDriverToLoadId(
                    currentLoadId,
                    driverItem.driverName,
                    driverItem.driverId,
                    isPrimaryDriver,
                    driverName || '',
                ),
            );
            onClickBack(isSplitLoad);
        }
    };

    const [assignDriver, { loading: mutationLoading }] = useMutation<AssignDriverToLoad>(ASSIGN_DRIVER_TO_LOAD, {
        onCompleted: onAssignDriver,
        refetchQueries: [
            {
                query: GET_DRIVERS_IN_STANDBY,
                variables: {
                    jobId: jobId,
                    lofaId: lofaId,
                },
            },
            {
                query: GET_ALL_DRIVERS_BY_REGION,
                variables: {
                    regionId: regionId,
                },
            },
        ],
    });

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <Error error={error} />;
    }

    let filteredList: (Driver | null)[] | null | undefined = data && data.getAllDrivers;
    if (isFromAddLoads && driverIds && driverIds.length > 0) {
        filteredList =
            filteredList &&
            filteredList.filter(driver => {
                return driver && !driverIds.includes(driver.driverId);
            });
    }
    if (searchPhrase && searchPhrase.length > 0) {
        filteredList =
            filteredList &&
            filteredList.filter(driver => {
                return driver && driver.driverName.toLowerCase().includes(searchPhrase.toLowerCase());
            });
    }
    const primaryTitle = isFromLoads ? 'Assign a Driver' : 'Add a Driver';
    const title = isSplitLoad ? 'Assign a Driver to a Split Load' : primaryTitle;

    const onClickAssign = async (driverId, thisDriverName, loadId): Promise<void> => {
        setDriverItem({ driverName: thisDriverName, driverId: driverId });
        setIsAssigning(true);
        if (isFromAddLoads) {
            await dispatch(Actions.removeDriverFromLoad(loadId));
            await dispatch(
                Actions.assignDriverToLoadId(
                    currentLoadId,
                    thisDriverName,
                    driverId,
                    isPrimaryDriver,
                    driverName || '',
                ),
            );
            await dispatch(Actions.addDriverIdToDriverList(driverId));
            onClickBack(isSplitLoad);
            setIsAssigning(false);
        } else {
            try {
                await assignDriver({
                    variables: { driverId: driverId, loadId: currentLoadId, isPrimaryDriver: isPrimaryDriver },
                });
            } finally {
                setIsAssigning(false);
            }
        }
    };
    // await dispatch(Actions.setCreatedLoadIds([loadId ?? -1]));
    // await dispatch(Actions.assignDriverToLoadId(loadId, driverName, driverId, true, driverName));

    // // onClickBack();
    // setShowDispatchModal(true);
    // await assignDriver({
    //     variables: { driverId: driverId, loadId: loadId, isPrimaryDriver: true },
    // });
    // };

    return (
        <Modal show={show} onHide={onClickHide} dialogClassName="AddDriverModal-Container">
            {/* <Modal show={show} onHide={onClickHide} scrollable size="lg" backdrop="static"> */}
            <Modal.Header className="AddDriverModal-Header">
                <Modal.Title className="AddDriverModal-Header-Container">
                    <ModalHeaderTitle
                        title={title}
                        onCancelClick={onClickHide}
                        hasBackButton={!isFromLoads}
                        backButtonTitle="Add Loads"
                        onBackClick={onClickBack}
                        load={loadId}
                        proppantName={proppantName}
                        lofa={lofa}
                        jobName={wellhead}
                        sandPO={sandPO}
                    ></ModalHeaderTitle>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="AddDriverModal-BodyContainer">
                <Row className='assignDriver-row'>
                    <span className="AssignDriverModal-LabelText mt-3">Standby Drivers in Job Pool</span>
                </Row>
                <div className="Scrollable-Table">
                    <Table striped bordered className="standbyDriversTable">
                        <thead className="Scrollable-Table-Item">
                            <tr className="Scrollable-Table-Item">
                                <th>Driver Name</th>
                                <th>Carrier Name</th>
                                <th>Minutes Since Load Requested</th>
                                <th>Minutes Since Load Rejected</th>
                                <th>Minutes Since Load Delivered</th>
                                <th>Distance to LOFA in miles</th>
                                <th className="AssignDriver-Btn-Container"></th>
                            </tr>
                        </thead>
                        <tbody data-bind="foreach: teams">
                            {standbyDrivers.length > 0 ? (
                                standbyDrivers.map(i => (
                                    <tr key={i['driverName']} className="Scrollable-Table-Item">
                                        <td>
                                            {i.driverPoolNotes ? (
                                                <>
                                                    {i.driverName}
                                                    <span
                                                        className="conv-icon on-shift DriverItem-BellyDumpIcon"
                                                        title={
                                                            i.checkInWithBellyDump
                                                                ? 'Checked in with belly dump (hopper)'
                                                                : 'Checked in with box'
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={[
                                                                'fas',
                                                                i.checkInWithBellyDump ? 'dumpster' : 'cube',
                                                            ]}
                                                            color="#b6bd00"
                                                        />
                                                    </span>
                                                    {i.isAvailable === false && (
                                                        <span
                                                            className="conv-icon off-shift DriverItem-BellyDumpIcon"
                                                            title="Driver not available"
                                                        >
                                                            <FontAwesomeIcon icon={['fas', 'ban']} color="#FF0000" />
                                                        </span>
                                                    )}
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        delay={{ show: 250, hide: 400 }}
                                                        overlay={
                                                            <span className="AssignStandByDriverModal-DirectionsTooltip">
                                                                {i.driverPoolNotes}
                                                            </span>
                                                        }
                                                    >
                                                        <i className="material-icons AssignStandByDriverModal-InfoIcon">
                                                            info
                                                        </i>
                                                    </OverlayTrigger>
                                                </>
                                            ) : (
                                                <>
                                                    {i['driverName']}
                                                    <span
                                                        className="conv-icon on-shift DriverItem-BellyDumpIcon"
                                                        title={
                                                            i.checkInWithBellyDump
                                                                ? 'Checked in with belly dump (hopper)'
                                                                : 'Checked in with box'
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={[
                                                                'fas',
                                                                i.checkInWithBellyDump ? 'dumpster' : 'cube',
                                                            ]}
                                                            color="#b6bd00"
                                                        />
                                                    </span>
                                                    {i.isAvailable === false && (
                                                        <span
                                                            className="conv-icon off-shift DriverItem-BellyDumpIcon"
                                                            title="Driver not available"
                                                        >
                                                            <FontAwesomeIcon icon={['fas', 'ban']} color="#FF0000" />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>{i.carrierName}</td>
                                        <td>{i.loadRequested}</td>
                                        <td>{i.loadRejected}</td>
                                        <td>{i.loadDelivered}</td>
                                        <td>{i.distanceToLofa}</td>
                                        <td className="AssignDriver-Btn-Container">
                                            <button
                                                className="AssignDriver-Btn"
                                                onClick={() => {
                                                    setSearchPhrase('');
                                                    onClickAssign(
                                                        i['driverId'],
                                                        i['driverName'],
                                                        parseInt(loadId ?? ''),
                                                    );
                                                }}
                                            >
                                                Assign Driver
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} align={'center'}>
                                        No Standby Drivers Available
                                    </td>
                                    <td className="AssignDriver-Btn-Container"></td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                <Row>
                    <span className="AssignDriverModal-LabelText mt-3">All Drivers</span>
                </Row>
                <div className="AddDriverModal-SearchBar">
                    <input
                        type="text"
                        placeholder="Search"
                        className="SearchBar-Input AssignDriver-SearchBar-Input"
                        onChange={event => setSearchPhrase(event.target.value)}
                    />
                </div>
                <hr className="AddDriverModal-Seperator"></hr>
                <div className="AddDriverModal-LoadList-Container">
                    {filteredList && filteredList.length > 0 ? (
                        filteredList.map(
                            (driver: Driver | null): React.ReactElement => (
                                <DriverItem
                                    disabled={isAssigning}
                                    setDisabled={setIsAssigning}
                                    key={driver!.driverId}
                                    driverId={driver!.driverId}
                                    driverName={driver!.driverName}
                                    driverCompany={driver!.driverCompany}
                                    onAssignClick={onClickBack}
                                    isSplitLoad={isSplitLoad}
                                    loadId={parseInt(loadId || '')}
                                    primaryDriverName={driverName}
                                    regionId={regionId || 0}
                                    refetch={refetch}
                                    isFromAddLoads={isFromAddLoads}
                                    trainingStatus={driver!.trainingStatus}
                                    checkInWithBellyDump={driver!.checkInWithBellyDump}
                                ></DriverItem>
                            ),
                        )
                    ) : (
                        <div className="AddDriverModal-LoadList-Container">
                            <NoResult isModal={true} />
                        </div>
                    )}
                </div>
                <DispatchModal
                    isFromLoads={isFromLoads}
                    isSplitLoad={false}
                    show={showDispatchModal}
                    onClickHide={() => setShowDispatchModal(false)}
                />
            </Modal.Body>
        </Modal>
    );
};

export default AddDriverModal;

